import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

import { Stat, formatPrice } from "./admin/shared";
import UploadForm from "./admin/UploadForm";
import FolderUpload from "./admin/FolderUpload";
import ZipUpload from "./admin/ZipUpload";
import PhotosList from "./admin/PhotosList";
import DiscountsPanel from "./admin/DiscountsPanel";
import SalesList from "./admin/SalesList";
import EventsManager from "./admin/EventsManager";
import SettingsManager from "./admin/SettingsManager";
import FtpImportPanel from "./admin/FtpImportPanel";
import PhotographersManager from "./admin/PhotographersManager";
import PayoutsPanel from "./admin/PayoutsPanel";
import MySettings from "./admin/MySettings";
import PhotographerDashboard from "./admin/PhotographerDashboard";

export default function AdminDashboard({ mode = "admin" }) {
  const { user } = useAuth();
  const isSuper = user && (user.role === "admin" || user.role === "super_admin");
  const isPhotographer = user && user.role === "photographer";
  const status = user?.status || "approved";

  const [tab, setTab] = useState(mode === "studio" ? "dashboard" : (isSuper ? "photographers" : "dashboard"));
  const [images, setImages] = useState([]);
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState({ orders: [], total_revenue: 0, total_sales: 0, total_commission: 0, total_photographer_payout: 0 });

  const refresh = async () => {
    try {
      const [imgs, evs, sl] = await Promise.all([
        api.get("/admin/images").catch(() => ({ data: [] })),
        api.get("/admin/events").catch(() => ({ data: [] })),
        api.get("/admin/sales").catch(() => ({ data: { orders: [], total_revenue: 0, total_sales: 0, total_commission: 0, total_photographer_payout: 0 } })),
      ]);
      setImages(imgs.data);
      setEvents(evs.data);
      setSales(sl.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { refresh(); }, []);

  if (isPhotographer && status !== "approved") {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-12 mt-16 pb-24" data-testid="pending-approval">
        <div className="pm-slash mb-6 text-sm"><span>Application {status}</span></div>
        <h1 className="font-display text-5xl uppercase leading-tight">
          {status === "pending" ? <>Under <span className="pm-orange">review</span></> : status === "rejected" ? <>Application <span className="pm-orange">declined</span></> : <>Account <span className="pm-orange">suspended</span></>}
        </h1>
        <p className="text-white/70 font-condensed mt-4">
          {status === "pending"
            ? "Thanks for applying! We usually approve accounts within 48 hours. You can set up your watermark below in the meantime."
            : "Contact hello@photomoto.co.uk if you think this is an error."}
        </p>
        {status === "pending" && (
          <div className="mt-8"><MySettings /></div>
        )}
      </div>
    );
  }

  const tabs = [];
  if (isSuper) tabs.push({ k: "photographers", l: "Photographers" });
  if (isSuper) tabs.push({ k: "events", l: "Events" });
  if (isPhotographer) tabs.push({ k: "dashboard", l: "Dashboard" });
  tabs.push({ k: "upload", l: "Upload" });
  if (isSuper) tabs.push({ k: "ftp", l: "FTP Import" });
  tabs.push({ k: "works", l: "Photos" });
  tabs.push({ k: "sales", l: "Sales" });
  tabs.push({ k: "discounts", l: "Discounts" });
  if (isSuper) tabs.push({ k: "payouts", l: "Payouts" });
  tabs.push({ k: "profile", l: isSuper ? "Site Settings" : "My Profile" });

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="admin-dashboard">
      <div className="border-b-2 border-[var(--pm-orange-40)] pb-6 mb-10">
        <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">
          {isSuper ? "Admin Console" : "Photographer Studio"}
        </p>
        <h1 className="font-display text-4xl md:text-6xl uppercase mt-2">
          {isSuper ? "PhotoMoto Admin" : `Welcome, ${user.company_name || user.name}`}
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Stat label={isSuper ? "Events" : "My photos"} value={isSuper ? events.length : images.filter(i => i.is_active).length} />
        <Stat label="Sales" value={sales.total_sales} />
        <Stat label="Revenue" value={formatPrice(sales.total_revenue)} />
        <Stat label={isSuper ? "Commission" : "Your payout"} value={formatPrice(isSuper ? sales.total_commission : sales.total_photographer_payout)} />
      </div>

      {/* Tabs */}
      <div className="flex gap-6 md:gap-8 border-b-2 border-white/10 mb-8 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`pb-4 text-xs uppercase tracking-[0.25em] font-semibold border-b-2 transition ${
              tab === t.k ? "border-[var(--pm-orange)] pm-orange" : "border-transparent text-white/50 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "photographers" && isSuper && <PhotographersManager />}
      {tab === "events" && isSuper && <EventsManager events={events} onChange={refresh} />}
      {tab === "dashboard" && isPhotographer && <PhotographerDashboard />}
      {tab === "upload" && <UploadPanel events={events} refresh={refresh} />}
      {tab === "ftp" && isSuper && <FtpImportPanel events={events} onImported={refresh} />}
      {tab === "works" && <PhotosList images={images} events={events} onChange={refresh} />}
      {tab === "sales" && <SalesList sales={sales} isSuper={isSuper} />}
      {tab === "discounts" && <DiscountsPanel events={events} isSuper={isSuper} />}
      {tab === "payouts" && isSuper && <PayoutsPanel />}
      {tab === "profile" && (isSuper ? <SettingsManager /> : <MySettings />)}
    </div>
  );
}

function UploadPanel({ events, refresh }) {
  const [mode, setMode] = React.useState("files"); // files | folder | zip
  return (
    <div className="space-y-6" data-testid="upload-panel">
      <div className="flex items-center gap-2 border border-white/10 bg-[#0a0a0a] p-2 w-fit" data-testid="upload-mode-toggle">
        <button
          onClick={() => setMode("files")}
          className={`text-xs uppercase tracking-[0.3em] px-6 py-3 ${mode === "files" ? "bg-[var(--pm-orange)] text-black font-bold" : "text-white/60 hover:text-white"}`}
          data-testid="upload-mode-files"
        >
          Files
        </button>
        <button
          onClick={() => setMode("folder")}
          className={`text-xs uppercase tracking-[0.3em] px-6 py-3 ${mode === "folder" ? "bg-[var(--pm-orange)] text-black font-bold" : "text-white/60 hover:text-white"}`}
          data-testid="upload-mode-folder"
        >
          Folder
        </button>
        <button
          onClick={() => setMode("zip")}
          className={`text-xs uppercase tracking-[0.3em] px-6 py-3 ${mode === "zip" ? "bg-[var(--pm-orange)] text-black font-bold" : "text-white/60 hover:text-white"}`}
          data-testid="upload-mode-zip"
        >
          Zip
        </button>
        <p className="text-xs text-white/50 font-condensed ml-4 pr-3 hidden md:block">
          {mode === "folder"
            ? "Upload a whole folder — classify each subfolder to its own class."
            : mode === "zip"
              ? "Upload one zip and walk away — the server processes it in the background."
              : "Pick individual files — same event & class for all."}
        </p>
      </div>
      {mode === "files" && <UploadForm events={events} onUploaded={refresh} />}
      {mode === "folder" && <FolderUpload events={events} onUploaded={refresh} />}
      {mode === "zip" && <ZipUpload events={events} onUploaded={refresh} />}
    </div>
  );
}
